<template>
  <v-layout align-start>
    <v-flex>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Videos / Preguntas</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-text-field class="text-xs-center" v-model="search" append-icon="search" label="Búsqueda" single-line hide-details></v-text-field>
        <v-btn color="primary" dark class="ml-3" @click="openDialog()">
          <v-icon left>add</v-icon>Nuevo
        </v-btn>
      </v-toolbar>

      <v-tabs v-model="typeTab" class="mb-2">
        <v-tab>Todos</v-tab>
        <v-tab>Videos / Podcast</v-tab>
        <v-tab>Preguntas (Referentes Responden)</v-tab>
      </v-tabs>

      <v-data-table :headers="headers" :items="filteredVideos" :search="search" class="elevation-1">
        <template v-slot:item.thumbnailUrl="{ item }">
          <img v-if="item.thumbnailUrl" :src="item.thumbnailUrl" style="height:48px;width:80px;object-fit:cover;border-radius:4px;margin:4px 0;" />
          <v-icon v-else color="grey darken-1">videocam</v-icon>
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 'qa' ? 'deep-purple' : 'blue-grey'" dark>
            {{ item.type === 'qa' ? 'Pregunta' : 'Video' }}
          </v-chip>
        </template>
        <template v-slot:item.title="{ item }">
          <div v-if="item.type === 'qa'">
            <div class="font-weight-medium">{{ item.question || '(sin pregunta)' }}</div>
            <div class="caption grey--text">{{ item.title }}</div>
          </div>
          <span v-else>{{ item.title }}</span>
        </template>
        <template v-slot:item.platform="{ item }">
          <v-chip small :color="platformColor(item.platform)" dark>{{ platformLabel(item.platform) }}</v-chip>
        </template>
        <template v-slot:item.views="{ item }">
          <span style="display:flex;align-items:center;gap:4px;">
            <v-icon small color="grey">visibility</v-icon>
            <span class="font-weight-medium">{{ item.views || 0 }}</span>
          </span>
        </template>
        <template v-slot:item.active="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small :color="item.active ? 'green' : 'grey'" style="cursor:pointer" v-on="on" @click="toggle('active', item)">
                {{ item.active ? 'visibility' : 'visibility_off' }}
              </v-icon>
            </template>
            <span>{{ item.active ? 'Desactivar' : 'Activar' }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.featured="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small :color="item.featured ? 'amber' : 'grey'" style="cursor:pointer" v-on="on" @click="toggle('featured', item)">
                {{ item.featured ? 'star' : 'star_border' }}
              </v-icon>
            </template>
            <span>{{ item.featured ? 'Quitar destacado' : 'Destacar' }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.opciones="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on" @click="openDialog(item)">edit</v-icon>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small color="red" v-on="on" @click="confirmDelete(item)">delete</v-icon>
            </template>
            <span>Eliminar</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="pa-4 text-center grey--text">No hay videos registrados</div>
        </template>
      </v-data-table>

      <v-dialog v-model="dialog" max-width="680" persistent>
        <v-card>
          <v-card-title class="headline primary white--text">{{ isEditing ? 'Editar' : 'Nuevo' }}</v-card-title>
          <v-card-text class="pt-4">
            <v-btn-toggle v-model="form.type" mandatory class="mb-4" color="primary">
              <v-btn value="video">
                <v-icon left small>ondemand_video</v-icon>Video / Podcast
              </v-btn>
              <v-btn value="qa">
                <v-icon left small>quiz</v-icon>Pregunta (Referentes Responden)
              </v-btn>
            </v-btn-toggle>

            <v-textarea
              v-if="form.type === 'qa'"
              v-model="form.question"
              label="Pregunta que responde el video *"
              outlined
              rows="2"
              hint="Se muestra superpuesta en la card, ej: '¿Cómo se financia el partido?'"
              persistent-hint
              class="mb-2"
            ></v-textarea>

            <v-text-field v-model="form.title" :label="form.type === 'qa' ? 'Título interno (no se muestra en el sitio)' : 'Título *'" outlined></v-text-field>
            <v-text-field
              v-model="form.url"
              label="URL del video *"
              outlined
              :hint="form.type === 'qa' ? 'YouTube Short o Reel de Instagram' : 'YouTube, Spotify o cualquier URL'"
              persistent-hint
              class="mb-2"
            ></v-text-field>
            <v-textarea v-if="form.type === 'video'" v-model="form.description" label="Descripción" outlined rows="3"></v-textarea>
            <v-combobox
              v-model="form.category"
              :items="categorySuggestions"
              :label="form.type === 'qa' ? 'Tema (para filtrar en el sitio)' : 'Categoría'"
              outlined
            ></v-combobox>
            <v-text-field v-model="form.thumbnailUrl" label="URL de miniatura (opcional)" outlined></v-text-field>
            <v-switch v-model="form.featured" label="Destacado"></v-switch>

            <div v-if="embedPreviewUrl" class="mt-2 mb-2">
              <div class="caption grey--text mb-1">Vista previa del embed</div>
              <iframe :src="embedPreviewUrl" width="100%" height="200" frameborder="0" allowfullscreen style="border-radius:6px;"></iframe>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancelar</v-btn>
            <v-btn color="primary" @click="save()" :disabled="!canSave">{{ isEditing ? 'Guardar' : 'Crear' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";
import swal from "sweetalert";

const EMPTY_FORM = { type: "video", title: "", question: "", url: "", description: "", category: "otro", thumbnailUrl: "", featured: false };

export default {
  data() {
    return {
      search: "",
      dialog: false,
      isEditing: false,
      editingItem: null,
      typeTab: 0,
      form: { ...EMPTY_FORM },
      headers: [
        { text: "Miniatura", value: "thumbnailUrl", sortable: false },
        { text: "Tipo", value: "type", sortable: true },
        { text: "Título / Pregunta", value: "title", sortable: true },
        { text: "Plataforma", value: "platform", sortable: true },
        { text: "Categoría", value: "category", sortable: true },
        { text: "Vistas", value: "views", sortable: true },
        { text: "Activo", value: "active", sortable: true },
        { text: "Destacado", value: "featured", sortable: true },
        { text: "Opciones", value: "opciones", sortable: false },
      ],
    };
  },
  created() {
    this.getVideos();
  },
  computed: {
    ...mapState("videoNamespace", ["videos"]),
    filteredVideos() {
      if (this.typeTab === 1) return this.videos.filter((v) => v.type !== "qa");
      if (this.typeTab === 2) return this.videos.filter((v) => v.type === "qa");
      return this.videos;
    },
    categorySuggestions() {
      const fromData = this.videos.map((v) => v.category).filter(Boolean);
      const defaults = ["entrevista", "debate", "acto", "podcast", "campaña", "otro"];
      return [...new Set([...defaults, ...fromData])];
    },
    canSave() {
      if (!this.form.title || !this.form.url) return false;
      if (this.form.type === "qa" && !this.form.question) return false;
      return true;
    },
    embedPreviewUrl() {
      const url = this.form.url;
      if (!url) return null;
      const igMatch = url.match(/instagram\.com\/(reel|p)\/([^/?]+)/);
      if (igMatch) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed/`;
      const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
      if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
      const spMatch = url.match(/open\.spotify\.com\/(episode|show|track)\/([A-Za-z0-9]+)/);
      if (spMatch) return `https://open.spotify.com/embed/${spMatch[1]}/${spMatch[2]}`;
      return null;
    },
  },
  methods: {
    ...mapActions("videoNamespace", ["getVideos", "saveVideo", "updateVideo", "toggleActive", "toggleFeatured", "deleteVideo"]),
    platformColor(platform) {
      if (platform === "youtube") return "red";
      if (platform === "spotify") return "green";
      if (platform === "instagram") return "purple";
      return "blue-grey";
    },
    platformLabel(platform) {
      if (platform === "youtube") return "YouTube";
      if (platform === "spotify") return "Spotify";
      if (platform === "instagram") return "Instagram";
      return "Otro";
    },
    openDialog(item) {
      if (item) {
        this.isEditing = true;
        this.editingItem = item;
        this.form = {
          type: item.type || "video",
          title: item.title || "",
          question: item.question || "",
          url: item.url || "",
          description: item.description || "",
          category: item.category || "otro",
          thumbnailUrl: item.thumbnailUrl || "",
          featured: !!item.featured,
        };
      } else {
        this.isEditing = false;
        this.editingItem = null;
        this.form = { ...EMPTY_FORM, type: this.typeTab === 2 ? "qa" : "video" };
      }
      this.dialog = true;
    },
    async save() {
      const payload = { ...this.form };
      if (payload.type !== "qa") delete payload.question;
      if (this.isEditing) {
        await this.updateVideo({ ...payload, _id: this.editingItem._id });
      } else {
        await this.saveVideo(payload);
      }
      this.dialog = false;
    },
    toggle(field, item) {
      if (field === "active") {
        this.toggleActive({ id: item._id || item.id, current: item.active });
      } else {
        this.toggleFeatured({ id: item._id || item.id });
      }
    },
    confirmDelete(item) {
      swal({
        title: "¿Estás seguro?",
        text: `Se eliminará "${item.type === 'qa' ? item.question : item.title}".`,
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) this.deleteVideo(item._id || item.id);
      });
    },
  },
};
</script>
